// Central wording for notices.
//
// Before this, every member of staff wrote a notice from scratch: the same trip produced a different
// letter depending on who typed it. A school keeps one set of templates, and issuing a notice fills
// one in -- but the text stays editable afterwards, because a template that cannot be adjusted just
// gets abandoned the first time a notice does not fit it.
//
// Placeholders are plain {{name}} tokens. Deliberately not an expression language: a school
// secretary maintains these, and anything that can compute can also fail in a way that is hard to
// explain to the person who wrote it.

export const PLACEHOLDERS = [
  { token: 'schoolName', label: 'School name' },
  { token: 'activityTitle', label: 'Activity / notice title' },
  { token: 'dates', label: 'Dates, comma separated' },
  { token: 'dateCount', label: 'Number of dates' },
  { token: 'time', label: 'Start–end time' },
  { token: 'venue', label: 'Venue' },
  { token: 'replyDueDate', label: 'Reply due date' },
  // Resolved per recipient when the rows are written, so each family reads its own child's name.
  // Costs nothing: one row per student already exists.
  { token: 'studentName', label: 'Student name (per recipient)' },
  { token: 'className', label: 'Class (per recipient)' },
];

const PER_RECIPIENT = new Set(['studentName', 'className']);

// Replaces every {{token}} it knows about. An unknown token is left alone rather than blanked --
// a stray "{{venu}}" in the output tells the author what they typed wrong, whereas an empty gap
// tells them nothing.
export const fillTemplate = (body, values) => {
  if (!body) return '';
  return body.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, token) => (
    Object.prototype.hasOwnProperty.call(values, token) ? String(values[token] ?? '') : match
  ));
};

// The values known at issue time, shared by every recipient.
export const noticeValues = ({ schoolName, title, dates, time, venue, replyDueDate }) => {
  const list = [...(dates || [])].sort();
  return {
    schoolName: schoolName || '',
    activityTitle: title || '',
    dates: list.join(', '),
    dateCount: String(list.length),
    time: time || '',
    venue: venue || '',
    replyDueDate: replyDueDate || '',
  };
};

// True if the text still needs per-recipient values filled in. Used to decide whether the fanned-out
// rows carry their own copy of the body or simply share the notice's.
export const hasPerRecipientTokens = (body) =>
  [...PER_RECIPIENT].some((t) => new RegExp(`\\{\\{\\s*${t}\\s*\\}\\}`).test(body || ''));

export const recipientValues = (student) => ({
  studentName: student?.name || '',
  className: student?.currentClass || student?.className || '',
});
